import {
  CommonStore,
  ICommonAppState,
  IUserModel,
  PlatformType,
  TimeHelper,
  UserService,
} from "@xala/common-services";
import {
  Button,
  Icon,
  ITableColumnProps,
  NotificationService,
  PageContent,
  PageHeader,
  Table,
  Text,
} from "@xala/common-ui";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PlatformTypeHelper, useTableDataProvider } from "../../../../helpers";
import { IDeviceModel } from "@xala/common-services/src/models/User/IUserDevicesModel";

const notificationService = NotificationService.getInstance();
const userService = new UserService().promisify();

export interface IUserDevicesProps {
  user: IUserModel;
}

type ColumnValue = string | number | boolean;

const TwoLineText: React.FC<{title: string | undefined, subtitle: string | undefined}> = ({ title, subtitle }) => <div>
  <Text style={{ marginBottom: 0, fontWeight: 500, color: "#fff" }}>{title ?? "-"}</Text>
  <Text ellipsis style={{ width: "100%" }}>{subtitle ?? "No data"}</Text>
</div>;

export const UserDevices: React.FC<IUserDevicesProps> = ({
  user,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  /* const {
    assetTypes: { data: assetTypesData = [] },
    state.applicationConfiguration.screensTypes
  } = useSelector(AssetStore.Selectors.assetSelector); */

  const platforms = useSelector((state: ICommonAppState) => state.common.platforms);

  useEffect(() => {
    dispatch(CommonStore.Actions.selectPlatforms());
  }, [dispatch]);

  const {
    dataLoader: userDevicesLoader,
    filters,
    pagination,
  } = useTableDataProvider({
    filtersSchema: {
      FullTextSearch: "string",
      UserId: "number",
      AssetId: "number",
      AssetTitle: "string",
      AssetTypes: "strings",
      UpToDate: "boolean",
    },
    loader: () =>
      userService.getDevices(user.Id, {PageSize: 9999}),
    deps: [user.Id],
    onError: (error) =>
      notificationService.error({
        message: t("LOADING_DATA_ERROR_MESSAGE"),
        description: error.Message,
      }),
  });

  const userDevicesData = userDevicesLoader.data?.Entities;

  const getColumnsProps = (): Array<
    ITableColumnProps<IDeviceModel>
  > => {
    return [
      {
        key: "PlatformCode",
        dataIndex: "PlatformCode",
        title: t("Platform", "PLATFORM"),
        filters: platforms.map((platform) => ({
          text: platform.DisplayName,
          value: platform.Code,
        })),
        filteredValue: filters.asTableArray.Platforms,
        onFilter: (value: ColumnValue, row: IDeviceModel) => {
          return value === row.PlatformCode;
        },
        render: (platformCode: PlatformType) => (
          PlatformTypeHelper.getTag(platformCode)
        ),
      },
      {
        key: "Name",
        dataIndex: "Name",
        title: t("Device", "DEVICE"),
        ellipsis: true,
        render: (name?: string) =>{
          let [deviceName, deviceId] = name?.split(" ") || [];

          deviceId = deviceId?.replace("(", "").replace(")", "");

          return (
            <TwoLineText title={deviceName} subtitle={deviceId} />
          )
        },
      },
      {
        key: "FirstLoginDate",
        dataIndex: "FirstLoginDate",
        title: t("FirstAccess", "FIRST ACCESS"),
        sorter: (a, b) => {
          if (!a.FirstLoginDate || !b.FirstLoginDate) {
            return 0;
          }

          return TimeHelper.compareIsBefore(
            TimeHelper.format(a.FirstLoginDate, "YYYY-MM-DD HH:mm:ss"),
            TimeHelper.format(b.FirstLoginDate, "YYYY-MM-DD HH:mm:ss")
          ) ? -1 : 1;
        },
        render: (firstLoginDate?: string) => {
          if (!firstLoginDate) {
            return <span>-</span>;
          }

          const date = TimeHelper.format(firstLoginDate, "DD/MM/YYYY");
          const time = TimeHelper.format(firstLoginDate, "HH:mm:ss");

          return <TwoLineText title={date} subtitle={time} />
        }
      },
      {
        key: "LastLoginDate",
        dataIndex: "LastLoginDate",
        title: t("LastAccess", "LAST ACCESS"),
        sorter: (a, b) => {
          if (!a.FirstLoginDate || !b.FirstLoginDate) {
            return 0;
          }

          return TimeHelper.compareIsBefore(
            TimeHelper.format(a.FirstLoginDate, "YYYY-MM-DD HH:mm:ss"),
            TimeHelper.format(b.FirstLoginDate, "YYYY-MM-DD HH:mm:ss")
          ) ? -1 : 1;
        },
        render: (lastLoginDate?: string) => {
          if (!lastLoginDate) {
            return <span>-</span>;
          }

          const date = TimeHelper.format(lastLoginDate, "DD/MM/YYYY");
          const time = TimeHelper.format(lastLoginDate, "HH:mm:ss");

          const subtitle = TimeHelper.fromNow(TimeHelper.format(lastLoginDate, "YYYY-MM-DD HH:mm:ss")) + " " + time;

          return <TwoLineText title={date} subtitle={subtitle} />
        }
      },
      {
        key: "LastIpAddress",
        dataIndex: "LastIpAddress",
        title: t("IpAddress", "IP ADDRESS"),
      },
      /* {
        key: "IsActive",
        dataIndex: "IsActive",
        title: t("Status", "STATUS"),
        filters: [
          {
            text: t("DEVICE_STATUS_ACTIVE", "Active"),
            value: true,
          },
          {
            text: t("DEVICE_STATUS_INACTIVE", "Inactive"),
            value: false,
          },
        ],
        filteredValue: filters.asTableArray.IsActive,
        onFilter: (value: ColumnValue, row: IDeviceModel) => {
          return value === row.IsActive;
        },
        render: (isActive?: boolean) => {
          return <Tag color={isActive ? "green" : "red"}>{isActive ? t("DEVICE_STATUS_ACTIVE", "Active") : t("DEVICE_STATUS_INACTIVE", "Inactive")}</Tag>;
        }
      } */
    ];
  };

  return (
    <PageContent
      className="UserDevices"
    >
      <PageHeader
        title="Devices List"
        extra={[
          <div
           key="total-devices"
           style={{ color: "#fff", backgroundColor: "#000", padding: 7, paddingInline: 15, borderRadius: 5
          }}>
            Total devices: <b style={{color:"#0094FF", paddingLeft: 5}}>{pagination.props.total}</b>
          </div>,
          <Button
            key="reload"
            shape="circle"
            icon={<Icon type="reload" />}
            onClick={userDevicesLoader.refresh}
            title={t("BUTTON_REFRESH_TITLE", "Refresh data")}
          />,
        ]}
      />
      <Table<IDeviceModel>
        columns={getColumnsProps()}
        rowKey="PaymentId"
        dataSource={userDevicesData}
        loading={userDevicesLoader.loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) =>
            t("ITEMS_RANGE_LABEL", {
              rangeFrom: range[0],
              rangeTo: range[1],
              total: total,
            }),
        }}
      />
    </PageContent>
  );
};
